import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import Modal from '@uikit/Modal/Modal';
import { CheckCircleOutlineIcon, LocationBoldIcon } from '@src/svg';
import { getLocationAsText, handleKeyDown, removeCounty } from '@src/utils';
import { useFormContext } from 'react-hook-form';
import {
  ControlledInput,
  ControlledSelect,
} from '@uikit/FormItems/ControlledItems';
import SubmitButton from '@uikit/FormItems/SubmitButton/SubmitButton';
import { useCreateOrUpdateLocationMutation } from '@store/rtk/global';
import { selectItemType } from '@src/types';
import { LocationModalProps } from '@src/modals/LocationModal/types';
import {
  useLazyGetCitiesQuery,
  useLazyGetStatesQuery,
} from '@store/rtk/coverageArea';
import { validations } from '@utils/validations';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { selectCoverageArea } from '@store/slices/coverageArea/coverageAreaSlice';
import {
  selectLocations,
  setRideLocationData,
  setSupervisorApprove,
} from '@store/slices/locations/locationsSlice';
import { selectSelects } from '@store/slices/selects/selects';
import { useLazyGetPlacesLocationTypesQuery } from '@store/rtk/selects';
import { toastError } from '@utils/helpers';
import { ControlledLocationInput } from '@uikit/FormItems/ControlledItems/ControlledLocationInput';
import MessageModal from '@src/modals/MessageModal/MessageModal';
import { useOverRide } from '@hooks/useOverRide';
import { BaseButton } from '@uikit/Button/BaseButton';
import { parseAddress, useGetCopyFunction } from './helper';
import { useLocationPin } from './useLocationPin';
import LocationMap from './LocationMap/LocationMap';
import { toastMessage } from '@utils/toastMessage';

const isVerifiedAddress = (location?: any) =>
  Boolean(location?.verifiedAddress ?? location?.verified);

const LocationModal: FC<LocationModalProps> = ({
  closeModal,
  override,
  success,
  error,
  setValue,
  change,
  index,
  setVerifiedAddress,
  searchInputValue = '',
  dataTestId,
}) => {
  const dispatch = useAppDispatch();
  const [createOrUpdateLocation] = useCreateOrUpdateLocationMutation();
  const [getCities, resultCities] = useLazyGetCitiesQuery();
  const [getStates] = useLazyGetStatesQuery();

  const [getPlacesLocationTypes] = useLazyGetPlacesLocationTypesQuery();

  const { states } = useAppSelector(selectCoverageArea);
  const { placesLocationTypes } = useAppSelector(selectSelects);
  const { shouldSupervisorApprove } = useAppSelector(selectLocations);

  const [messageToggle, setMessageToggle] = useState<{
    title: string;
    content: ReactNode | null;
    toggle: boolean;
  }>({
    title: '',
    content: '',
    toggle: false,
  });

  const {
    control,
    reset,
    getValues,
    watch,
    handleSubmit,
    setValue: setFormValue,
    formState: { dirtyFields, isSubmitting },
  } = useFormContext();

  const manualSelectedData = getValues();
  const { handleLatitudeChange } = useGetCopyFunction({ setFormValue });
  const { locationId, isOverrideObject, overrideData, exactLocationTitle } =
    useOverRide({
      reset,
      getValues,
      override,
    });

  const {
    pin,
    resetToken,
    message,
    seedFromValues,
    handlePinMove,
    hasPendingAddress,
    applyPendingAddress,
  } = useLocationPin({
    overrideData,
    getValues,
    setFormValue,
    watch,
    states,
    getStates,
  });

  const verified = watch('verified');
  const [line1, line2, cityText, stateObj, zipText] = watch([
    'address1',
    'address2',
    'city',
    'state',
    'zipCode',
  ]);
  const joinParts = (parts: unknown[], separator: string) =>
    parts
      .map((part) => String(part ?? '').trim())
      .filter(Boolean)
      .join(separator);
  const cardTitle = joinParts([line1, line2], ', ');
  const cardSubtitle = joinParts(
    [joinParts([cityText, stateObj?.label || stateObj?.value], ', '), zipText],
    ' '
  );
  const isManualCreate = !locationId && !overrideData && !verified;

  const handleSuccess = useCallback(
    (e: any = null) => {
      const values = getValues();
      setValue?.(getLocationAsText(values));
      setVerifiedAddress?.(isVerifiedAddress(e ?? values));
      change?.({ target: { name: 'location', value: e ?? values } });
      success && success('');
      !shouldSupervisorApprove && closeModal();
    },
    [getValues, setValue, change, success, closeModal]
  );

  const handleSendData = useCallback(
    async (data: any) => {
      dispatch(setRideLocationData(data));
      const { type, ...rest } = data;
      const exactType = getValues('type');
      const typeObj = {
        ...(exactType.id === 0
          ? { typeName: exactType.label, typeValue: exactType.value, type: 0 }
          : { type: exactType.id }),
      };
      try {
        const newData = {
          ...rest,
          state: getValues('state')?.value || '',
          country: getValues('country')?.value || '',
          city: getValues('city'),
          zipCode: getValues('zipCode'),
          verified: true,
          ...typeObj,
        };
        if (shouldSupervisorApprove) {
          return handleSuccess();
        }
        if (!locationId) {
          const res = await createOrUpdateLocation({
            body: newData,
            type: 'create',
          }).unwrap();

          if (res) {
            if (res?.exist) {
              setMessageToggle({
                title: 'Warning',
                content: (
                  <div
                    className="location_exist_warning"
                    data-test-id="location-exist-warning"
                  >
                    <p>
                      The address already exists please select the address below
                      to continue.
                    </p>
                    <p
                      className="address_link"
                      onClick={() => {
                        return handleSuccess(res);
                      }}
                      data-test-id="location-exist-warning-link"
                    >
                      {getLocationAsText(res)}
                    </p>
                  </div>
                ),
                toggle: true,
              });
            } else {
              return handleSuccess({
                ...res,
                phoneNumber: newData.phoneNumber,
              });
            }
          }
        } else {
          const res = await createOrUpdateLocation({
            body: newData,
            type: 'update',
            id: Number(String(locationId).match(/\d+/)?.[0]),
          }).unwrap();
          return handleSuccess({
            ...newData,
            ...(res && typeof res === 'object' ? res : {}),
            id: newData.id,
            phoneNumber: newData.phoneNumber,
          });
        }
      } catch (err) {
        toastError(err, toastMessage.PLACE.failedToSaveLocation);
        error?.('Failed to save location');
      }
    },
    [
      getValues,
      createOrUpdateLocation,
      handleSuccess,
      locationId,
      shouldSupervisorApprove,
    ]
  );

  const handleGetExactData = useCallback(
    async (
      selected: selectItemType,
      callBack: (obj: any) => any,
      field: 'city' | 'zipCode' | 'county'
    ) => {
      const queryBody = { [field]: { id: selected.id } };
      try {
        await callBack(queryBody).unwrap();
      } catch (err) {
        toastError(err, toastMessage.PLACE.failedToLoadLocationDetails);
        error?.('Failed to load location details');
      }
    },
    []
  );

  useEffect(() => {
    if (overrideData || !searchInputValue) {
      return;
    }

    const { businessName, address1, address2, city, state, zipCode } =
      parseAddress(searchInputValue);
    const normalizedState = state.trim().toUpperCase();

    setFormValue('businessName', businessName);
    setFormValue('address1', address1);
    setFormValue('address2', address2);
    setFormValue('city', city);
    setFormValue('zipCode', zipCode);
    setFormValue('country', '');

    const syncStateAndCounty = async () => {
      if (!normalizedState) {
        return;
      }

      const stateOptions = states.length
        ? states
        : await getStates({}).unwrap();
      const foundStateObj = stateOptions.find(
        (item) => item.label?.toUpperCase() === normalizedState
      );

      if (!foundStateObj) {
        return;
      }

      setFormValue('state', foundStateObj, {
        shouldValidate: true,
      });
      await handleGetExactData(foundStateObj, getCities, 'county');
    };

    void syncStateAndCounty();
  }, [
    overrideData,
    searchInputValue,
    setFormValue,
    states,
    getStates,
    getCities,
    handleGetExactData,
  ]);

  return (
    <Modal
      title={exactLocationTitle}
      titleIcon={<LocationBoldIcon width={20} height={20} color="#ffffff" />}
      closeClick={closeModal}
      needConfirm={false}
      maxWidth="720px"
      className="location_modal_for__location loc_modal__add"
    >
      {messageToggle.toggle && (
        <MessageModal
          closeModal={() => {
            setMessageToggle({
              title: '',
              content: null,
              toggle: false,
            });
          }}
          title={messageToggle.title}
          content={messageToggle.content}
          hasButton={false}
        />
      )}
      <div className="location_modal_for__location">
        <div className="form_for__location">
          <div className="input_text_for__location">
            <ControlledLocationInput
              dataTestId={`${dataTestId}-modal-location-address`}
              control={control}
              title="Location Address"
              name="physical_address"
              inputId={`physical_address_${Date.now()}`}
              tabIndex={1}
              defaultValue={
                overrideData
                  ? getLocationAsText(overrideData)
                  : searchInputValue
              }
              verified={!!overrideData || manualSelectedData?.verified}
              verifiedAddress={isVerifiedAddress(
                overrideData ?? manualSelectedData
              )}
              isInModal={true}
              index={index}
              change={(e) => {
                dispatch(setSupervisorApprove(true));
                reset({
                  ...e,
                  city: e.city.value,
                  zipCode: e.zipCode.value,
                  state: e.state,
                  country:
                    typeof e.country === 'string'
                      ? {
                          id: null,
                          label: removeCounty(e.country),
                          value: e.country,
                        }
                      : e.country
                        ? {
                            ...e.country,
                            label: removeCounty(
                              String(e.country?.label ?? e.country?.value)
                            ),
                          }
                        : e.country,
                });
                // New suggestion: its location is the new original location.
                seedFromValues();
              }}
              disabled={isOverrideObject(override) || !overrideData}
            />
          </div>
          <div className="input_text_for__location">
            <ControlledInput
              dataTestId={`${dataTestId}-modal-phone-number`}
              control={control}
              name="businessName"
              title="Business Name"
              tabIndex={2}
              rules={{ required: true }}
              disabled={!!locationId && !!overrideData?.businessName}
            />
          </div>
          <div className="input_text_for__location">
            <ControlledSelect
              dataTestId={`${dataTestId}-modal-location-type`}
              control={control}
              name="type"
              title="Location Type"
              tabIndex={8}
              selectOptions={placesLocationTypes}
              requestByAsync={() => getPlacesLocationTypes({})}
              disabled={!!locationId}
              rules={{
                validate: (value: any) => {
                  if (!isManualCreate) return true;
                  return validations.selectRequired('Location Type')(value);
                },
              }}
            />
          </div>

          <div className="input_text_for__location">
            <ControlledInput
              dataTestId={`${dataTestId}-modal-address1`}
              control={control}
              name="address1"
              title="Address Line 1"
              tabIndex={3}
              rules={{ required: true }}
              disabled={isOverrideObject(override)}
            />
          </div>
          <div className="input_text_for__location">
            <ControlledInput
              dataTestId={`${dataTestId}-modal-latitude`}
              control={control}
              name="latitude"
              title="Latitude"
              tabIndex={9}
              rules={{ required: true }}
              disabled={isOverrideObject(override)}
              handleKeyDown={handleKeyDown}
              onChange={handleLatitudeChange}
            />
          </div>
          <div className="input_text_for__location">
            <ControlledInput
              dataTestId={`${dataTestId}-modal-address2`}
              control={control}
              name="address2"
              title="Address Line 2"
              tabIndex={4}
              disabled={!!locationId}
            />
          </div>
          <div className="input_text_for__location">
            <ControlledInput
              dataTestId={`${dataTestId}-modal-longitude`}
              control={control}
              name="longitude"
              title="Longitude"
              tabIndex={10}
              rules={{
                required: validations.required('Longitude'),
              }}
              disabled={isOverrideObject(override)}
              handleKeyDown={handleKeyDown}
            />
          </div>
          <div className="input_text_for__location">
            <div className="bottom_input_text">
              <ControlledInput
                dataTestId={`${dataTestId}-modal-city`}
                control={control}
                name="city"
                title="City"
                rules={{
                  required: validations.required('City'),
                }}
                tabIndex={5}
                disabled={isOverrideObject(override)}
              />
            </div>
            <div className="bottom_input_text">
              <ControlledSelect
                dataTestId={`${dataTestId}-modal-state`}
                control={control}
                name="state"
                title="State"
                rules={{ validate: validations.selectRequired('State') }}
                selectOptions={states}
                disabled={isOverrideObject(override)}
                requestByAsync={() => getStates({})}
                changeSelect={(e) => handleGetExactData(e, getCities, 'county')}
                styles={{ gap: '0 !important' }}
                tabIndex={6}
              />
            </div>
            <div className="bottom_input_text">
              <ControlledInput
                dataTestId={`${dataTestId}-modal-zipCode`}
                control={control}
                name="zipCode"
                title="Zip Code"
                rules={{
                  required: validations.required('Zip Code'),
                }}
                disabled={isOverrideObject(override)}
                mask="99999"
                tabIndex={7}
              />
            </div>
          </div>
          <div className="input_text_for__location">
            <ControlledSelect
              dataTestId={`${dataTestId}-modal-county`}
              placeholder=""
              control={control}
              name="country"
              title="County"
              tabIndex={11}
              rules={{ validate: validations.selectRequired('County') }}
              disabled={isOverrideObject(override) || !watch('state').label}
              selectOptions={resultCities?.data}
            />
          </div>
        </div>
        <LocationMap
          position={pin}
          resetToken={resetToken}
          onPinMove={handlePinMove}
          title={cardTitle}
          subtitle={cardSubtitle}
          message={message}
          showMatchAddress={hasPendingAddress}
          onMatchAddress={applyPendingAddress}
          dataTestId={dataTestId}
        />
        <div className="location_modal__bottom_for__location">
          {isOverrideObject(override) ? (
            <div
              className="location_modal__verified"
              data-test-id={`${dataTestId}-modal-verified`}
            >
              <CheckCircleOutlineIcon width={44} height={44} color="#32936F" />
              <div className="location_modal__verified_text">
                <strong>Location Verified</strong>
                <span>
                  This location was verified using our trusted address API.
                </span>
              </div>
            </div>
          ) : (
            <p />
          )}
          <div className="location_modal__actions">
            <BaseButton
              dataTestId={`${dataTestId}-modal-cancel`}
              className="location_modal__cancel"
              variant="outline"
              title="Cancel"
              onClick={closeModal}
            />
            <SubmitButton
              dataTestId={`${dataTestId}-modal-save`}
              disableWhenUnchangedFields={!isOverrideObject(override)}
              loading={isSubmitting}
              disabled={isSubmitting}
              dirtyFields={dirtyFields}
              onClick={handleSubmit(handleSendData)}
              title="Save"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default LocationModal;
